import { isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, inject, OnInit, PLATFORM_ID, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EMPTY, Subject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { PaginationComponent } from '@components/pagination-component/pagination-component';

import { AuditLogsService } from '@services/audit-logs/audit-logs.service';
import { AuditAction, AuditEntityType, AuditLogItem, AuditLogsResponse } from '@services/audit-logs/interfaces/audit-logs.interfaces';
import { ToastService } from '@services/components/ToastService/toast.service';
import {
  AuditChangeLine,
  auditFieldLabel,
  buildAuditChangeLines,
  buildAuditLogClipboardText,
  formatAuditDateTime,
  toEndOfLocalDayIso,
  toStartOfLocalDayIso,
} from '@shared/utils/audit-log.utils';

import { AUDIT_ACTION_LABELS, AUDIT_ENTITY_TYPE_LABELS, AUDIT_LOGS_PAGE_SIZE } from '@constants/audit-logs.constants';
import { AUDIT_LOG_COPIED_TOAST, AUDIT_LOG_COPY_ERROR_TOAST } from '@constants/toasts.constants';

interface AuditFilterOption<TValue extends string> {
  value: TValue;
  label: string;
}

const toFilterOptions = <TValue extends string>(labels: Record<TValue, string>): AuditFilterOption<TValue>[] =>
  (Object.entries(labels) as [TValue, string][]).map(([value, label]) => ({ value, label }));

@Component({
  selector: 'app-activity-log',
  standalone: true,
  imports: [PaginationComponent],
  templateUrl: './activity-log.html',
  styleUrl: './activity-log.css',
})
export class ActivityLogComponent implements OnInit {
  public readonly auditLogs = signal<AuditLogItem[]>([]);
  public readonly isLoading = signal(false);
  public readonly hasLoadError = signal(false);
  public readonly currentPage = signal(1);
  public readonly totalPages = signal(1);
  public readonly entityTypeFilter = signal<AuditEntityType | ''>('');
  public readonly actionFilter = signal<AuditAction | ''>('');
  public readonly fromDateFilter = signal('');
  public readonly toDateFilter = signal('');

  public readonly entityTypeOptions = toFilterOptions(AUDIT_ENTITY_TYPE_LABELS);
  public readonly actionOptions = toFilterOptions(AUDIT_ACTION_LABELS);

  private readonly auditLogsService = inject(AuditLogsService);
  private readonly toastService = inject(ToastService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly destroyRef = inject(DestroyRef);
  private readonly loadRequests = new Subject<number>();

  ngOnInit(): void {
    this.loadRequests
      .pipe(
        switchMap((page) => {
          this.isLoading.set(true);
          this.hasLoadError.set(false);
          return this.auditLogsService
            .getAuditLogs(page, AUDIT_LOGS_PAGE_SIZE, {
              entityType: this.entityTypeFilter() || null,
              action: this.actionFilter() || null,
              from: toStartOfLocalDayIso(this.fromDateFilter()),
              to: toEndOfLocalDayIso(this.toDateFilter()),
            })
            .pipe(
              catchError(() => {
                this.handleLoadError();
                return EMPTY;
              }),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((response) => this.applyAuditLogs(response));

    this.loadPage(1);
  }

  public loadPage(page: number): void {
    this.currentPage.set(page);
    this.loadRequests.next(page);
  }

  public onEntityTypeChange(event: Event): void {
    this.entityTypeFilter.set((event.target as HTMLSelectElement).value as AuditEntityType | '');
    this.loadPage(1);
  }

  public onActionChange(event: Event): void {
    this.actionFilter.set((event.target as HTMLSelectElement).value as AuditAction | '');
    this.loadPage(1);
  }

  public onFromDateChange(event: Event): void {
    this.fromDateFilter.set((event.target as HTMLInputElement).value);
    this.loadPage(1);
  }

  public onToDateChange(event: Event): void {
    this.toDateFilter.set((event.target as HTMLInputElement).value);
    this.loadPage(1);
  }

  public clearFilters(): void {
    this.entityTypeFilter.set('');
    this.actionFilter.set('');
    this.fromDateFilter.set('');
    this.toDateFilter.set('');
    this.loadPage(1);
  }

  public hasActiveFilters(): boolean {
    return Boolean(this.entityTypeFilter() || this.actionFilter() || this.fromDateFilter() || this.toDateFilter());
  }

  public onPageChange(page: number): void {
    this.loadPage(page);
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  public formatDate(auditLog: AuditLogItem): string {
    return formatAuditDateTime(auditLog.createdAt);
  }

  public actionLabel(auditLog: AuditLogItem): string {
    return AUDIT_ACTION_LABELS[auditLog.action] ?? auditLog.action;
  }

  public entityTypeLabel(auditLog: AuditLogItem): string {
    return AUDIT_ENTITY_TYPE_LABELS[auditLog.entityType] ?? auditLog.entityType;
  }

  public changedFieldsLabel(auditLog: AuditLogItem): string {
    return auditLog.changedFields.map((fieldName) => auditFieldLabel(fieldName)).join(', ');
  }

  public changeLines(auditLog: AuditLogItem): AuditChangeLine[] {
    return buildAuditChangeLines(auditLog);
  }

  public copyAuditLog(auditLog: AuditLogItem): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const clipboard = window.navigator?.clipboard;
    if (!clipboard) {
      this.toastService.open(AUDIT_LOG_COPY_ERROR_TOAST);
      return;
    }

    clipboard
      .writeText(buildAuditLogClipboardText(auditLog))
      .then(() => this.toastService.open(AUDIT_LOG_COPIED_TOAST))
      .catch(() => this.toastService.open(AUDIT_LOG_COPY_ERROR_TOAST));
  }

  private applyAuditLogs(response: AuditLogsResponse): void {
    this.auditLogs.set(response.items);
    this.totalPages.set(Math.max(1, Math.ceil(response.total / response.pageSize)));
    this.isLoading.set(false);
  }

  private handleLoadError(): void {
    this.auditLogs.set([]);
    this.isLoading.set(false);
    this.hasLoadError.set(true);
  }
}
